import React from 'react';
import moment from 'moment';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import useGtm from 'Douleutaras/hooks/gtm';
import { useProGenderInfo } from 'Douleutaras/hooks/users/pro';
import { buildUrl, VALID_URLS } from 'Douleutaras/utilities/url-builder';
import { BID_MILESTONES, NEED_HELP_MILESTONES } from 'Douleutaras/constants/ccf/need-help-options';
import classNames from 'classnames';
import Tooltip from 'Douleutaras/components/Tooltip/Tooltip';

import { JobViewConcludedEventNames } from './settings/gtm';
import './DashboardBidPositiveCCF.scss';

export interface DashboardBidPositiveCCFProps {
  buyerSelectedMilestone: NEED_HELP_MILESTONES;
  reviewPosted: boolean;
  reviewAllowed: boolean;
  disableCCFPositive: boolean;
  milestonePosition: number;
  defaultAction: VoidFunction;
  jobId: string;
  bidId: string;
  dateUnformatted: string;
  isChatEmpty: boolean;
  index: number;
  sellerId: string;
  customText?: string;
  small?: boolean;
  outlined?: boolean;
}

const CtaButtonWrapper = ({ hideCCFCTA, defaultAction, children, small = false, outlined = false }) => (
    <button styleName={classNames('bid-card-footer__btn', {
      'bid-card-footer__btn--small': small,
      'bid-card-footer__btn--outlined': outlined
    })}
            type="button"
            data-tracking="ccf-positive-cta"
            disabled={hideCCFCTA}
            onClick={defaultAction}
    >
      <Tooltip content={children}>
        <span styleName="bid-card-footer__btn-title">
          {children}
        </span>
      </Tooltip>
    </button>
);

export const DashboardBidPositiveCCF: React.FC<DashboardBidPositiveCCFProps> = params => {

  const {
    buyerSelectedMilestone,
    reviewPosted,
    reviewAllowed,
    disableCCFPositive,
    milestonePosition,
    defaultAction,
    jobId,
    bidId,
    dateUnformatted,
    isChatEmpty,
    index,
    sellerId,
    customText,
    small = false,
    outlined = false
  } = params;

  const { track } = useGtm();
  const selectedBuyerMilestoneStep = BID_MILESTONES?.[buyerSelectedMilestone]?.step;
  const buyerMilestoneIsSetOrMoreAdvanced = selectedBuyerMilestoneStep >= milestonePosition;

  const sellerGenderInfo = useProGenderInfo(sellerId);

  if (customText) {
    return <CtaButtonWrapper hideCCFCTA={disableCCFPositive}
                             defaultAction={defaultAction}
                             small
    >{customText}
    </CtaButtonWrapper>;
  }

  // Change button literals based on conditions found here https://gitlab.com/d3454/core/douleutaras/-/issues/1367
  // and https://gitlab.com/d3454/core/douleutaras/-/issues/2340
  switch (milestonePosition) {
    case 2:
      if (!isChatEmpty || buyerMilestoneIsSetOrMoreAdvanced) {
        return <CtaButtonWrapper hideCCFCTA={disableCCFPositive} defaultAction={defaultAction} small outlined>
          <Trans values={sellerGenderInfo}>
            T.JOB.CCF-POSITIVE-MILESTONE-2-HAS-MESSAGES
          </Trans>
        </CtaButtonWrapper>;
      }

      return <CtaButtonWrapper hideCCFCTA={disableCCFPositive} defaultAction={defaultAction} small outlined>
        <Trans values={sellerGenderInfo}>
          T.JOB.CCF-POSITIVE-MILESTONE-2-EMPTY-CONVERSATION
        </Trans>
      </CtaButtonWrapper>;
    case 3:
      const isFuture = moment(dateUnformatted)
        .diff(new Date(), 'minutes') > 0;
      if (isFuture || buyerMilestoneIsSetOrMoreAdvanced) {
        return <CtaButtonWrapper hideCCFCTA={disableCCFPositive} defaultAction={defaultAction} small outlined>
          <Trans values={sellerGenderInfo}>
            T.JOB.CCF-POSITIVE-MILESTONE-3-FUTURE
          </Trans>
        </CtaButtonWrapper>;
      }

      return <CtaButtonWrapper hideCCFCTA={disableCCFPositive} defaultAction={defaultAction} small outlined>
        <Trans values={sellerGenderInfo}>
          T.JOB.CCF-POSITIVE-MILESTONE-3-PAST
        </Trans>
      </CtaButtonWrapper>;
    case 4:
      if (!isChatEmpty || buyerMilestoneIsSetOrMoreAdvanced) {
        return <CtaButtonWrapper hideCCFCTA={disableCCFPositive} defaultAction={defaultAction} small outlined>
          <Trans values={sellerGenderInfo}>
            T.JOB.CCF-POSITIVE-MILESTONE-4-HAS-MESSAGES
          </Trans>
        </CtaButtonWrapper>;
      }

      return <CtaButtonWrapper hideCCFCTA={disableCCFPositive} defaultAction={defaultAction} small outlined>
        <Trans values={sellerGenderInfo}>
          T.JOB.CCF-POSITIVE-MILESTONE-4-EMPTY-CONVERSATION
        </Trans>
      </CtaButtonWrapper>;

    case 6:
      return (
        <>
          {reviewAllowed && !disableCCFPositive &&
            <Link
              to={buildUrl(VALID_URLS.CLIENT_REVIEW_BID, jobId, bidId)}
              target="_blank"
              data-tracking="ccf-positive-cta"
              styleName={classNames('bid-card-footer__btn', {
                'bid-card-footer__btn--small': small,
                'bid-card-footer__btn--outlined': outlined
              })}
              onClick={() => track(JobViewConcludedEventNames.REVIEW_CLICKED)}
            >
            <span styleName="bid-card-footer__btn-title">
              <Trans>T.JOB.CCF.PRO-REVIEW-ALLOWED</Trans>
            </span>
            </Link>
          }
          {reviewPosted &&
            <Tooltip content={<Trans>T.JOB.CCF.PRO-REVIEW-POSTED</Trans>} direction="bottom">
              <span styleName={classNames('bid-card-footer__btn', 'bid-card-footer__btn--disabled', {
                'bid-card-footer__btn--small': small,
                'bid-card-footer__btn--outlined': outlined
              })}>
                <span styleName="bid-card-footer__btn-icon" className="dashicon dashicon-checkmark" />
                  <span styleName="bid-card-footer__btn-title">
                    <Trans>T.JOB.CCF.PRO-REVIEW-POSTED</Trans>
                  </span>
              </span>
            </Tooltip>
          }
        </>
      );
    default:
      return <CtaButtonWrapper hideCCFCTA={disableCCFPositive} defaultAction={defaultAction} small outlined>
        <Trans values={sellerGenderInfo}>
          T.JOB.CCF-POSITIVE
        </Trans>
      </CtaButtonWrapper>;
  }
};
